/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Theme as MaterialTheme, useTheme, Paper, Stack, TextField, Button, Container, SvgIcon, TableRow, TableCell, Typography, TableContainer, Table, TableHead, TableBody } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';
import { useState, useEffect, Dispatch, SetStateAction, } from "react";

import { ReactComponent as noDocument } from "../../../common/icons/noDocument.svg"
import { ReactComponent as document } from "../../../common/icons/document.svg"
import { ReactComponent as documents } from "../../../common/icons/documents.svg"

import { useAppBarHeight, useDebounce } from '../../../common/CustomHooks';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useParams } from 'react-router-dom';
import { IApproverUserModel } from '../../../service/model/IApproverModel';
import ApproverService from '../../../service/ApproverService';
import DialogApproversReplace from '../components/DialogApproversReplace';
import { IApproverUserGuiModel } from '../../../model/IApproverGuiModel';
import ApproverBusinessLogic from '../../../businessLogic/ApproverBusinessLogic';

const CreateRow = ({ approver, openDialog }:
    { approver: IApproverUserGuiModel, openDialog: (approver: IApproverUserGuiModel) => void }): JSX.Element => {

    const theme = useTheme();
    const { t, i18n } = useTranslation();

    const ShowDocType = ({ q }: { q: number }): JSX.Element => {
        if (q <= 0) {
            return <SvgIcon component={noDocument} color="error" sx={{ fontSize: "2rem" }} inheritViewBox />
        } else if (q === 1) {
            return <SvgIcon component={document} color="primary" sx={{ fontSize: "2rem" }} inheritViewBox />
        } else {
            return <SvgIcon component={documents} color="secondary" sx={{ fontSize: "2rem" }} inheritViewBox />
        }
    }

    return <>
        <TableRow>
            <TableCell>{approver.user.fullName}</TableCell>
            <TableCell>
                <Stack direction="row" alignItems="flex-end" spacing={2}>
                    <Typography variant="h6" color={theme.palette.info.main}>{approver.numberOfRequests}</Typography>
                    <ShowDocType q={approver.numberOfRequests} />
                </Stack>
            </TableCell>
            <TableCell>
                <Button disabled={approver.numberOfRequests < 1 ? true : false} variant="contained" onClick={() => openDialog(approver)}>
                    {t("basic.replace")}
                </Button>
            </TableCell>
        </TableRow>
    </>
}

const Approvers = (): JSX.Element => {

    const { organizationId } = useParams();
    const theme = useTheme();
    const { t, i18n } = useTranslation();

    const [open, setOpen] = useState(false);

    const [loader, setLoader] = useState(false);

    const [approvers, setApprovers] = useState<IApproverUserGuiModel[]>([]);
    const [chosenApprover, setChosenApprover] = useState<IApproverUserGuiModel | undefined>(undefined);

    const openDialog = (approver: IApproverUserGuiModel) => {
        setChosenApprover(approver);
        setOpen(true);
    }
    const closeDialog = (reason: "backdropClick" | "escapeKeyDown") => {
        if (reason !== "backdropClick")
            getData(true);
            setOpen(false);
    }

    useEffect(() => {
        let isMounted = true; //Avoid memory leaks. Async function running after dismount.
        
        //Close current window when searching for something new
        getData(isMounted);

        return () => { isMounted = false };
    }, [organizationId]);

    //###############################################################################################################################################
    //###############################################################################################################################################
    const getData = async (isMounted: boolean) => {
        const logic = new ApproverBusinessLogic();
        if (organizationId) {
            setLoader(true);
            const result = await logic.getApprovers(organizationId);
            setLoader(false);

            if (result.hasErrors) {
                setApprovers([]);
                toast("Error: " + result.errors.join(", "));
            } else {
                if (result.value.length > 0 && isMounted) {
                    setApprovers(result.value);
                }
            }
        }
    };
    //###############################################################################################################################################
    //###############################################################################################################################################
    const appBarHeight = useAppBarHeight();
    //########################################################################################
    //########################################################################################
    return <main>
        <Grid container direction="column" wrap="nowrap"
            sx={{
                padding: theme.spacing(2),
                width: "100%",
                height: {
                    xs: "calc(100vh - " + appBarHeight + "px )",
                    xl: "calc(100vh)"
                },
                //"& .MuiDataGrid-columnHeaders": { backgroundColor: theme.palette.info.main, color: "white" },
                //"& .MuiDataGrid-footerContainer": { backgroundColor: theme.palette.info.main, color: "white" }
            }}>
            {/*<Grid container spacing={2} padding={theme.spacing(2)}>*/}

            {/*</Grid>*/}
            <Container component={Paper} disableGutters sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <Grid container paddingBottom={theme.spacing(2.5)} flex="1 1 auto" overflow="hidden" minHeight="40rem">
                    <TableContainer
                        sx={{
                            border: 1,
                            borderColor: theme.palette.grey[200],
                            borderRadius: theme.shape.borderRadius,
                            maxHeight: "100%",
                        }}>
                        <Table aria-label="tableApprovers" stickyHeader sx={{ width: "100%" }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t("approver.approvers")}</TableCell>
                                    <TableCell>{t("approver.documentsInQueue")}</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {approvers.map(e => {
                                    return <CreateRow approver={e} openDialog={openDialog} />
                                })}
                            </TableBody>
                        </Table>
                        {chosenApprover ? <DialogApproversReplace
                            organizationId={organizationId ? organizationId : ""}
                            oldApprover={chosenApprover}
                            open={open}
                            closeDialog={closeDialog} /> : <></>}
                    </TableContainer>
                </Grid>
            </Container>
        </Grid>
    </main>

}
export default Approvers;